import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Grid,
  Button,
  Collapse,
  IconButton,
  Typography,
  MenuItem,
  LinearProgress,
  Divider,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import _ from "lodash";
import { OtaContext, FleetContext } from "../../context";
import { SectionGridContainer } from "../../components/Layout";
import { FilledSelect, DateTimeRangeInput, SlidingRangeInput } from "../../components/FormElements";
import { DropdownsByCategory, VehicleSearch } from "./FilterElements";

import {
  availableStates,
  deploymentStatuses,
  possessionStatuses,
  scooterTypes,
} from "../../constants";

const getCellStrengthCeil = (bars) => {
  switch (bars) {
    case 0:
      return -120;
    case 1:
      return -111;
    case 2:
      return -106;
    case 3:
      return -91;
    default:
      return undefined;
  }
};

const getCellStrengthFloor = (bars) => {
  switch (bars) {
    case 1:
      return -119;
    case 2:
      return -110;
    case 3:
      return -105;
    case 4:
      return -90;
    default:
      return undefined;
  }
};

const getCeilBarsFromCellStrength = (cellStrength) => {
  switch (cellStrength) {
    case -120:
      return 0;
    case -111:
      return 1;
    case -106:
      return 2;
    case -91:
      return 3;
    default:
      return undefined;
  }
};

const getFloorBarsFromCellStrength = (cellStrength) => {
  switch (cellStrength) {
    case -119:
      return 1;
    case -110:
      return 2;
    case -105:
      return 3;
    case -90:
      return 4;
    default:
      return undefined;
  }
};

const OtaFilter = () => {
  const theme = useTheme();
  const {
    otaVehicles,
    otaFilterParams,
    initialOtaFilterParams,
    setOtaFilterParams,
    fetchOtaVehicles,
    fetchFirmwareVersions,
    scVersions,
    mdVersions,
    bmsVersions,
    geofencePackages,
    firmwarePackages,
    geofenceRuleIds,
    geofenceMapIds,
    otaOrderingOptions,
    inProgressFpaCount,
    inProgressGpaCount,
    fetchInProgressOtaCount,
    MAX_FPA_PER_FLEET,
    MAX_GPA_PER_FLEET,
  } = useContext(OtaContext);
  const { activeFleet } = useContext(FleetContext);
  const { fleetId } = useParams();

  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  // eslint-disable-next-line max-len
  const [vehicleIdentifierDropdownValue, setVehicleIdentifierDropdown] =
    useState("vehicle_shortcode");

  const availableStatesList = Object.values(availableStates).filter((s) => s !== "mismatched");
  const [excludeFirmwareSelections, setExcludeFirmwareSelections] = useState(false);
  const [excludeGeofenceSelections, setExcludeGeofenceSelections] = useState(false);

  const defaultGeofencePackage = geofencePackages.find(
    (gfp) => gfp.url === activeFleet.default_geofence_package,
  );

  const defaultFirmwarePackage = firmwarePackages.find(
    (fwp) => fwp.url === activeFleet.default_firmware_package,
  );

  const handleFetchOtaVehicles = async () => {
    const keyToOmit =
      vehicleIdentifierDropdownValue === "vehicle_id" ? "shortcodes__shortcode__in" : "ids__in";
    setLoading(true);
    await fetchOtaVehicles(_.omit(otaFilterParams, [keyToOmit]));
    setLoading(false);
  };

  const getMenuData = async () => {
    setLoading(true);

    await Promise.all([fetchInProgressOtaCount(fleetId), fetchFirmwareVersions()]);
    setLoading(false);
  };

  const selectFieldPropsByType = {
    stateAndStatus: [
      { otaKey: "deployment_status", menuOptions: deploymentStatuses, multiSelect: true },
      { otaKey: "possession_status", menuOptions: possessionStatuses, multiSelect: true },
      { otaKey: "target_state", menuOptions: availableStatesList, multiSelect: true },
      { otaKey: "last_reported_state", menuOptions: availableStatesList, multiSelect: true },
    ],
    firmware: [
      {
        otaKey: "systemcontroller__firmware_version",
        menuOptions: scVersions,
        markAsDefault: defaultFirmwarePackage?.sc?.version || null,
      },
      {
        otaKey: "motorcontroller__firmware_version",
        menuOptions: mdVersions,
        markAsDefault: defaultFirmwarePackage?.md?.version || null,
      },
      {
        otaKey: "battery__firmware_version",
        menuOptions: bmsVersions,
        markAsDefault: defaultFirmwarePackage?.bms.version || null,
      },
      { otaKey: "firmware_package_application__in_progress", isBoolean: true },
    ],
    geofences: [
      {
        otaKey: "geofence_rules_id",
        menuOptions: geofenceRuleIds,
        markAsDefault: defaultGeofencePackage?.rules_id || null,
      },
      {
        otaKey: "geofence_map_id",
        menuOptions: geofenceMapIds,
        markAsDefault: defaultGeofencePackage?.map_id || null,
      },
      { otaKey: "geofence_package_application__in_progress", isBoolean: true },
    ],
  };

  useEffect(() => {
    if (!(scVersions && mdVersions && bmsVersions && geofenceRuleIds && geofenceMapIds))
      getMenuData();
    setOtaFilterParams(JSON.parse(localStorage.getItem(`${fleetId}-otaFilterParams`)));

    if (localStorage.getItem(`${fleetId}-expandFilters`) === null) setShowFilters(true);
    else setShowFilters(JSON.parse(localStorage.getItem(`${fleetId}-expandFilters`)));
  }, []);

  useEffect(() => {
    localStorage.setItem(`${fleetId}-otaFilterParams`, JSON.stringify(otaFilterParams));
  }, [otaFilterParams, fleetId]);

  useEffect(() => {
    setOtaFilterParams({ fleets: fleetId });
    localStorage.setItem(`${fleetId}-expandFilters`, showFilters);
  }, [showFilters, fleetId]);

  const resetFilters = () => {
    setOtaFilterParams({ ...initialOtaFilterParams, fleets: fleetId });
    setExcludeFirmwareSelections(false);
    setExcludeGeofenceSelections(false);
  };

  return (
    <SectionGridContainer spaceContents style={{ padding: theme.spacing(1, 0, 1, 2) }}>
      <Grid item container xs={12} spacing={1} alignItems="flex-start">
        <Grid item md={4} xs={11}>
          <Typography variant="h5">Filter Fleet Vehicles for OTA</Typography>
          <Typography variant="subtitle2">{`(${
            (inProgressFpaCount && inProgressFpaCount[fleetId]) || "0"
          }/${MAX_FPA_PER_FLEET}) FPA in progress`}</Typography>
          <Typography variant="subtitle2">{`(${
            (inProgressGpaCount && inProgressGpaCount[fleetId]) || "0"
          }/${MAX_GPA_PER_FLEET}) GPA in progress`}</Typography>
        </Grid>
        <Grid container item md={8} xs={1} justify="flex-end" spacing={1}>
          <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? (
              <ExpandLessIcon fontSize="large" />
            ) : (
              <ExpandMoreIcon fontSize="large" />
            )}
          </IconButton>
        </Grid>
      </Grid>
      <Collapse
        in={showFilters}
        style={!showFilters ? { paddingBottom: "0px", width: "100%" } : { width: "100%" }}
      >
        <Grid container item xs={12}>
          {/* row 1 */}
          <VehicleSearch
            searchById={vehicleIdentifierDropdownValue === "vehicle_id"}
            searchText={
              vehicleIdentifierDropdownValue === "vehicle_id"
                ? otaFilterParams.ids__in
                : otaFilterParams.shortcodes__shortcode__in
            }
            handleSearchByChange={setVehicleIdentifierDropdown}
            handleSearchTextChange={setOtaFilterParams}
          />
          {/* row 2 */}
          <Grid container item spacing={2} xs={12} style={{ marginTop: isMobile ? "0" : "-24px" }}>
            <DateTimeRangeInput
              label="Last Ingest"
              floor={otaFilterParams.last_ingest__gte}
              ceil={otaFilterParams.last_ingest__lte}
              onChangeFloor={(value) => setOtaFilterParams({ last_ingest__gte: value })}
              onChangeCeil={(value) => setOtaFilterParams({ last_ingest__lte: value })}
            />
            <SlidingRangeInput
              label="Cell Strength (Bars)"
              floor={getFloorBarsFromCellStrength(otaFilterParams.cell_strength__gte)}
              ceil={getCeilBarsFromCellStrength(otaFilterParams.cell_strength__lte)}
              min={0}
              max={4}
              onChangeFloor={(value) => {
                setOtaFilterParams({
                  cell_strength__gte: getCellStrengthFloor(value),
                });
              }}
              onChangeCeil={(value) => {
                setOtaFilterParams({
                  cell_strength__lte: getCellStrengthCeil(value),
                });
              }}
            />
            <SlidingRangeInput
              label="Battery Charge (%)"
              unit="%"
              floor={otaFilterParams.battery__charge__gte}
              ceil={otaFilterParams.battery__charge__lte}
              showDivider={!isMobile}
              onChangeFloor={(value) => setOtaFilterParams({ battery__charge__gte: value })}
              onChangeCeil={(value) => setOtaFilterParams({ battery__charge__lte: value })}
            />
          </Grid>
          {/* row 3 */}
          <Grid container item spacing={2} xs={12} style={{ marginTop: isMobile ? "0" : "-24px" }}>
            <Grid container item xs={12} md={3}>
              <FilledSelect
                label={t("OTA.Filters.scooter_type")}
                value={otaFilterParams.scooter_type__in}
                onChange={(e) => {
                  setOtaFilterParams({
                    scooter_type__in: e.target.value,
                  });
                }}
                SelectProps={{ multiple: true }}
                FormControlProps={{ fullWidth: true, margin: "normal" }}
              >
                {Object.keys(scooterTypes).map((scooterType) => (
                  <MenuItem key={scooterType} value={scooterType}>
                    {scooterType}
                  </MenuItem>
                ))}
              </FilledSelect>
            </Grid>
          </Grid>
        </Grid>
        <DropdownsByCategory
          title="stateAndStatus"
          otaSelectFieldsProps={selectFieldPropsByType.stateAndStatus}
        />
        <DropdownsByCategory
          title="firmware"
          otaSelectFieldsProps={selectFieldPropsByType.firmware}
          excludable
          excludeSelection={excludeFirmwareSelections}
          setExcludeSelection={setExcludeFirmwareSelections}
        />
        <DropdownsByCategory
          title="geofences"
          otaSelectFieldsProps={selectFieldPropsByType.geofences}
          excludable
          excludeSelection={excludeGeofenceSelections}
          setExcludeSelection={setExcludeGeofenceSelections}
        />
      </Collapse>
      <Grid item xs={12} style={{ marginRight: theme.spacing(2) }}>
        <Divider />
      </Grid>
      {/* Buttons Row */}
      <Grid
        container
        item
        xs={12}
        spacing={2}
        justify="space-between"
        alignItems="center"
        style={{ marginRight: isMobile ? 0 : theme.spacing(3) }}
      >
        <Grid item xs={6} md={8}>
          <Button
            onClick={resetFilters}
            variant="outlined"
            style={{ border: "1px solid white" }}
            fullWidth={isMobile}
          >
            {t("OTA.Filters.resetFilters")}
          </Button>
        </Grid>
        <Grid container item xs={6} md={2} justify={isMobile ? "" : "flex-end"}>
          <FilledSelect
            label={t("OTA.Filters.ordering")}
            value={otaFilterParams.ordering}
            fullWidth
            SelectProps={{
              id: "order-results-by",
              multiple: true,
              displayEmpty: true,
              style: { background: theme.palette.background.paper },
            }}
            onChange={(e) => {
              setOtaFilterParams({
                ordering: e.target.value,
              });
            }}
            FormControlProps={{
              fullWidth: true,
              margin: "none",
              size: "small",
              variant: "outlined",
            }}
          >
            {otaOrderingOptions.map((ord) => (
              <MenuItem key={ord} value={ord}>
                {ord}
              </MenuItem>
            ))}
          </FilledSelect>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            onClick={handleFetchOtaVehicles}
            style={{
              marginLeft: isMobile ? 0 : theme.spacing(2),
              background: theme.palette.background.overlay,
              color: theme.palette.text.primary,
            }}
            fullWidth
            variant="contained"
          >
            {otaVehicles ? "REFRESH VEHICLES" : "GET VEHICLES"}
          </Button>
        </Grid>
      </Grid>

      {loading && (
        <Grid item xs={12} style={{ padding: "0px" }}>
          <LinearProgress color="secondary" />
        </Grid>
      )}
    </SectionGridContainer>
  );
};

export default OtaFilter;
